'use client';

/*
 * SHARED LEGACY THEME PRESENTATION (moved here from components/Footer in Phase 8, code unchanged).
 * Marble.Theme.01 and Marble.Theme.02 render this footer identically. It carries the Phase 7.1 payment-icon fix
 * (xmlns on every data-URI SVG). Not Storefront Core: a future theme may replace it.
 */
import Link from 'next/link';
import { useState } from 'react';
import { createTranslator } from '@/lib/localization';
import { menuItemHref } from '@/lib/storefront-links';

// Local menu-item type. menuItemHref comes from lib/storefront-links (client-safe, Storefront
// Core) — NOT from lib/public-site.ts, which also imports next/headers (server-only); pulling
// any real (non-type) export from it into this 'use client' component would drag that
// server-only import into the client bundle and break the build.
type FooterMenuItem = {
  kind: 'page' | 'category' | 'custom';
  label: string;
  targetSlug?: string;
  url?: string;
  children?: FooterMenuItem[];
};

type FooterMenu = {
  menuKey: string;
  name: string;
  items: FooterMenuItem[];
};

type FooterCorporateInfo = {
  companyName?: string;
  footerCopyrightText?: string;
};

type Props = {
  texts?: Record<string, string> | null;
  /** Defaults to true (every existing page keeps the newsletter block). The homepage passes
   * false — its "subscribe" section is being retired in favor of admin-managed Home Text
   * Cards, but the Footer component itself (and every other page that renders it) is
   * unaffected. */
  showNewsletter?: boolean;
  corporateInfo?: FooterCorporateInfo | null;
  /** Every active, published menu at the Footer location (any number of them, admin-managed
   * from /admin/menus) — one column each. Replaces the previous hard-coded About/Shop/
   * Support/Legal columns; a 5th footer menu needs no frontend code change. */
  footerMenus?: FooterMenu[];
  activeLanguage?: string;
};

export function LegacyFooter({ texts, showNewsletter = true, corporateInfo, footerMenus = [], activeLanguage = 'en' }: Props) {
  const t = createTranslator(texts ?? {});
  const currentYear = new Date().getFullYear();
  const copyrightText = corporateInfo?.footerCopyrightText?.trim() || corporateInfo?.companyName;
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterStatus('loading');
    
    try {
      // TODO: Connect to backend newsletter API
      // For now, just simulate
      await new Promise(resolve => setTimeout(resolve, 1000));
      setNewsletterStatus('success');
      setEmail('');
      setTimeout(() => setNewsletterStatus('idle'), 3000);
    } catch (error) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 3000);
    }
  };

  return (
    <footer className="bg-gray-1 border-t border-gray-3">
      {/* Newsletter Section */}
      {showNewsletter ? (
        <div className="bg-primary-light-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="heading-4 text-dark mb-2">{t('footer.newsletterTitle')}</h3>
                <p className="text-dark-3 text-sm">{t('footer.newsletterSubtitle')}</p>
              </div>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  placeholder={t('footer.newsletterPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 px-4 py-3 text-sm border border-gray-3 rounded-lg focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === 'loading'}
                  className="px-6 py-3 bg-primary text-white font-semibold text-sm rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {newsletterStatus === 'loading' ? t('footer.newsletterSubmitting') : t('footer.newsletterSubmit')}
                </button>
              </form>
              {newsletterStatus === 'success' && (
                <p className="text-sm text-success col-span-2">{t('footer.newsletterSuccess')}</p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div
          className="grid gap-8 mb-8"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          {/* One column per admin-managed Footer-location menu — any number of them, no
              frontend code change needed to add/remove a column (see PublicSiteLayout /
              public-site.ts's fetchMenusByLocation). Menu.Name is the column heading. */}
          {footerMenus.map((menu) => (
            <div key={menu.menuKey}>
              <h4 className="font-semibold text-dark-2 mb-4">{menu.name}</h4>
              <ul className="space-y-3">
                {menu.items.map((item, index) => (
                  <li key={`${menu.menuKey}-${index}`}>
                    <Link
                      href={menuItemHref(item, activeLanguage)}
                      className="text-dark-3 hover:text-primary text-sm transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Social */}
          <div>
            <h4 className="font-semibold text-dark-2 mb-4">{t('footer.followUs')}</h4>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition-colors"
                title="Facebook"
              >
                f
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition-colors"
                title="Twitter"
              >
                𝕏
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition-colors"
                title="Instagram"
              >
                📷
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition-colors"
                title="LinkedIn"
              >
                in
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-3 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-dark-4 text-sm">
            &copy; {currentYear} {copyrightText ? `${copyrightText} ` : ''}{t('footer.allRightsReserved')}.
          </p>
          <div className="flex gap-4">
            <img
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='20' viewBox='0 0 32 20'%3E%3Crect x='1' y='1' width='30' height='18' rx='2' fill='%231434CB'/%3E%3Ctext x='16' y='14' text-anchor='middle' fill='white' font-size='10' font-weight='bold'%3EVisa%3C/text%3E%3C/svg%3E"
              alt="Visa"
              className="h-6"
            />
            <img
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='20' viewBox='0 0 32 20'%3E%3Crect x='1' y='1' width='30' height='18' rx='2' fill='%23EB001B'/%3E%3Ccircle cx='11' cy='10' r='4' fill='%23FF5F00'/%3E%3Ccircle cx='21' cy='10' r='4' fill='%23FF5F00'/%3E%3C/svg%3E"
              alt="Mastercard"
              className="h-6"
            />
            <img
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='20' viewBox='0 0 32 20'%3E%3Crect x='1' y='1' width='30' height='18' rx='2' fill='%23003087'/%3E%3Ctext x='16' y='14' text-anchor='middle' fill='white' font-size='10' font-weight='bold'%3EPayPal%3C/text%3E%3C/svg%3E"
              alt="PayPal"
              className="h-6"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}
